<template>
  <div id="HomeView" class="container max-w-4xl mx-auto pt-10 pb-20 px6">
    <div class="text-center">
      <router-link to="/" class="text-blue-500 no-underline hover:underline">
        Scan Out
      </router-link>
    </div>
    <div v-if="notice != null" class="text-green-700 text-center">{{ notice }}</div>
    <div v-if="error != null" class="text-red-700 text-center">{{ error }}</div>
    <div class="flex flex-wrap mt-4 mb-6">
      <div class="w-full px-3">
        <TextInput 
            label="Order Number"
            id="order_num"
            placeholder="12345"
            v-model:input="orderNum"
            inputType="text"
            @keyup.enter="searchOrders()"
        />
      </div>
    </div>

    <div class="flex flex-wrap mt-8 mb-6">
      <div class="w-full px-3">
        <SubmitFormButton 
            btnText="Search"
            @click="searchOrders()"
        />
      </div>
    </div>

    <!-- Orders found by order number -->
    <div v-if="orders.length > 0" class="mt-8">
      <h3 class="text-lg font-semibold mb-4">Orders with Order Number {{ searchedOrderNum }}</h3>
      <div class="grid gap-4">
        <div v-for="order in orders" :key="order.order_id" 
             class="bg-white rounded-lg shadow p-4 hover:shadow-lg transition-shadow">
          <div class="grid grid-cols-2 gap-2">
            <div class="text-gray-600">Order Number:</div>
            <div class="font-medium">{{ order.order_id }}</div>
            
            <div class="text-gray-600">Name:</div>
            <div class="font-medium">{{ order.ship_to_name }}</div>
            
            <div class="text-gray-600">Scan Date:</div>
            <div class="font-medium">{{ new Date(order.created_at).toLocaleDateString() }}</div>
            
            <div class="text-gray-600">UID Code:</div>
            <div class="font-medium">
              <template v-if="getUidCodes(order).length > 0">
                <span
                  v-for="(code, idx) in getUidCodes(order)"
                  :key="`${code}-${idx}`"
                >
                  {{ code }}<span v-if="idx < getUidCodes(order).length - 1">, </span>
                </span>
              </template>
              <template v-else>no scan</template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Sections for each UID code -->
    <div v-for="uidCode in sortedUidCodes" :key="uidCode" class="mt-8">
      <h3 class="text-lg font-semibold mb-4">Orders with UID Code {{ uidCode }}</h3>
      <div class="grid gap-4">
        <div v-for="order in ordersByUidCode[uidCode]" :key="order.order_id" 
             class="bg-white rounded-lg shadow p-4 hover:shadow-lg transition-shadow">
          <div class="grid grid-cols-2 gap-2">
            <div class="text-gray-600">Order Number:</div>
            <div class="font-medium">{{ order.order_id }}</div>
            
            <div class="text-gray-600">Name:</div>
            <div class="font-medium">{{ order.ship_to_name }}</div>
            
            <div class="text-gray-600">Scan Date:</div>
            <div class="font-medium">{{ new Date(order.created_at).toLocaleDateString() }}</div>
            
            <div class="text-gray-600">UID Code:</div>
            <div class="font-medium">
              <template v-if="getUidCodes(order).length > 0">
                <span
                  v-for="(code, idx) in getUidCodes(order)"
                  :key="`${code}-${idx}`"
                >
                  {{ code }}<span v-if="idx < getUidCodes(order).length - 1">, </span>
                </span>
              </template>
              <template v-else>no scan</template>
            </div>
          </div>
        </div>
      </div>
    </div>
    
  </div>
</template>

<script setup>
  import axios from 'axios'
  import { onMounted, ref, computed } from 'vue'
  // import Swal from 'sweetalert2'
  import TextInput from '@/components/global/TextInput.vue'
  import SubmitFormButton from '@/components/global/SubmitFormButton.vue'

  let orderNum = ref(null)
  let searchedOrderNum = ref(null)
  let orders = ref([])
  let ordersByUidCode = ref({})
  let notice = ref(null)
  let error = ref(null)

  onMounted(async () => {
      // do nothing
  })

  const searchOrders = async () => {
    try {
      notice.value = null
      error.value = null
      ordersByUidCode.value = {}
      
      searchedOrderNum.value = orderNum.value
      let res = await axios.get(process.env.VUE_APP_API_URL + 'api/v1/search_outbound_orders_by_order_num/' + orderNum.value)
      console.log(res.data)
      orders.value = res.data
      
      if (orders.value.length > 0) {
        notice.value = "At least 1 order was scanned with order number " + orderNum.value
        
        // Extract all UID codes from the found orders
        const allUidCodes = new Set()
        orders.value.forEach(order => {
          const uidCodes = getUidCodes(order)
          uidCodes.forEach(code => allUidCodes.add(code))
        })
        
        // For each UID code, fetch all orders with that code
        if (allUidCodes.size > 0) {
          const uidCodesArray = Array.from(allUidCodes).sort()
          
          // Fetch orders for each UID code
          for (const uidCode of uidCodesArray) {
            try {
              const uidRes = await axios.post(process.env.VUE_APP_API_URL + 'api/v1/oders_with_uid_codes', {
                uid_codes: [uidCode]
              })
              
              // Store all orders for this UID code (no filtering)
              ordersByUidCode.value[uidCode] = uidRes.data || []
            } catch (uidErr) {
              // If UID code search fails for this code, just continue
              console.error(`Error searching for orders with UID code ${uidCode}:`, uidErr)
              ordersByUidCode.value[uidCode] = []
            }
          }
        }
      } else {
        error.value = "No orders were scanned with order number " + orderNum.value
      }
      orderNum.value = null
    } catch (err) {
      // nothing
    }
  }

  const getUidCodes = (order) => {
    try {
      const items = Array.isArray(order?.outbound_order_items) ? order.outbound_order_items : []
      return items
        .map((i) => i?.uid_code)
        .filter((c) => typeof c === 'string' && c.trim().length > 0)
        .sort()
    } catch (err) {
      return []
    }
  }

  // Computed property to get sorted UID codes for iteration
  const sortedUidCodes = computed(() => {
    return Object.keys(ordersByUidCode.value).sort()
  })
</script>
